import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  VStack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Eye, EyeOff, Lock, Download } from 'lucide-react';

/**
 * Reusable dialog for encrypted exports.
 * Shows password fields and offers encrypted or plain export.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Called when the modal is closed
 * @param {function} onExport - Called with password (string) or null (no encryption)
 * @param {string} title - Dialog title (e.g., "Exportar Laudo")
 * @param {boolean} isLoading - Whether the export is in progress
 */
export default function ExportPasswordDialog({ isOpen, onClose, onExport, title = 'Exportar Dados', isLoading = false }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleEncryptedExport = () => {
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    setError('');
    onExport(password);
  };

  const handlePlainExport = () => {
    setError('');
    onExport(null);
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setError('');
    setShowPassword(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Por segurança, recomendamos exportar com criptografia.
                O arquivo será protegido com senha e salvo como ZIP.
              </Text>
            </Alert>

            <FormControl>
              <FormLabel>Senha de criptografia</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Digite uma senha"
                />
                <InputRightElement>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl isInvalid={confirmPassword.length > 0 && password !== confirmPassword}>
              <FormLabel>Confirmar senha</FormLabel>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Repita a senha"
              />
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <FormErrorMessage>As senhas não coincidem</FormErrorMessage>
              )}
            </FormControl>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handlePlainExport}
            isDisabled={isLoading}
            leftIcon={<Download size={16} />}
          >
            Exportar sem senha
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleEncryptedExport}
            isLoading={isLoading}
            isDisabled={password.length === 0 || password !== confirmPassword}
            leftIcon={<Lock size={16} />}
          >
            Exportar com senha
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
